'use es6';

import { createSelector } from 'reselect';
import { Map as ImmutableMap } from 'immutable';
import { RequestStatusRecord } from 'AdsUI/records/RequestStatus';
import ReportingDataRecord from 'AdsUI/records/reporting/ReportingDataRecord';
import CampaignFilterRecord from 'AdsUI/records/reporting/CampaignFilterRecord';
import { EntityTypes, ALL_STATUSES } from 'AdsUI/lib/Constants';
import { getAdGroupIdFromParams, getParams, getLocationFromProps, getNetworkFromParams, getSequenceIdFromParams } from 'AdsUI/selectors/PropsSelectors';
import { getGlobalFilters } from './GlobalFilters';
import ChildrenReportRecord from 'AdsUI/records/reporting/ChildrenReportRecord';
import { convertTimeRangeToKey } from 'AdsUI/lib/Utils';
export const getCampaignEntities = state => state.reporting.campaignEntities;
export const getCampaignReports = state => state.reporting.campaignReports;
export const getChildEntities = state => state.reporting.childEntities;
export const getChildReports = state => state.reporting.childReports;
export const getTrackingStatuses = state => state.reporting.trackingStatuses;
export const getCampaignDetails = createSelector(getCampaignEntities, getCampaignReports, getTrackingStatuses, (entities, reports, trackingStatuses) => reports.map((report, filter) => {
  const entityFilter = filter.set('timeRange', null).set('attributionView', null);
  return ReportingDataRecord({
    entity: entities.get(entityFilter),
    report,
    trackingStatus: trackingStatuses.get(entityFilter)
  });
}));
export const getCampaignDetailsRequestStatus = state => state.requestStatus.reportingCampaignDetails;
export const getCampaignDetailsEntityFilter = createSelector(getNetworkFromParams, getParams, (network, {
  campaignId
}) => CampaignFilterRecord({
  entityId: campaignId,
  network
}));
export const getCampaignDetailsReportFilter = createSelector(getNetworkFromParams, getParams, getGlobalFilters, (network, {
  campaignId
}, {
  timeRange,
  attributionView
}) => CampaignFilterRecord({
  entityId: campaignId,
  network,
  timeRange,
  attributionView
}));
export const getCampaignForCampaignDetails = createSelector(getCampaignDetailsEntityFilter, getCampaignDetailsReportFilter, getCampaignEntities, getCampaignReports, getGlobalFilters, getTrackingStatuses, (entityFilter, reportFilter, campaignEntities, campaignReports, trackingStatuses) => new ReportingDataRecord({
  entity: campaignEntities.get(entityFilter),
  report: campaignReports.get(reportFilter),
  trackingStatus: trackingStatuses.get(entityFilter)
}));
export const getCampaignRequestStatus = createSelector(getCampaignDetailsRequestStatus, getCampaignDetailsReportFilter, (campaignDetailsRequestStatus, campaignDetailsFilter) => campaignDetailsRequestStatus.get(campaignDetailsFilter, new RequestStatusRecord()));
export const getChildrenByEntity = createSelector(getChildEntities, getChildReports, getTrackingStatuses, (entities, reports, trackingStatuses) => {
  return reports.map((childReports, {
    network,
    entityId,
    entityType,
    statuses
  }) => childReports.reportMap.map((report, childId) => {
    const entity = entities.getIn([CampaignFilterRecord({
      network,
      entityType,
      entityId,
      statuses
    }), childId]);
    return ReportingDataRecord({
      entity,
      report,
      trackingStatus: trackingStatuses.get(CampaignFilterRecord({
        network,
        entityType,
        entityId: entity.id
      }))
    });
  }));
});
export const getEntityData = createSelector(getParams, getLocationFromProps, (params, {
  pathname
}) => {
  if (pathname.indexOf(EntityTypes.NEGATIVE_KEYWORD) !== -1) {
    return {
      entityType: EntityTypes.NEGATIVE_KEYWORD,
      entityId: params.adGroupId
    };
  } else if (pathname.indexOf(EntityTypes.KEYWORD) !== -1) {
    return {
      entityType: EntityTypes.KEYWORD,
      entityId: params.adGroupId
    };
  } else if (params.adGroupId || params.adSetId) {
    return {
      entityType: EntityTypes.AD,
      entityId: params.adGroupId || params.adSetId
    };
  } else {
    return {
      entityType: EntityTypes.ADGROUP,
      entityId: params.campaignId
    };
  }
});
export const getCampaignChildrenFilter = createSelector(getGlobalFilters, getEntityData, getNetworkFromParams, getParams, ({
  timeRange,
  attributionView,
  statuses
}, {
  entityId,
  entityType
}, network, {
  sequenceId
}) => {
  return CampaignFilterRecord({
    timeRange,
    entityId,
    entityType: entityType === EntityTypes.NEGATIVE_KEYWORD ? EntityTypes.KEYWORD : entityType,
    network,
    attributionView,
    statuses: !!sequenceId || entityType === EntityTypes.NEGATIVE_KEYWORD ? ALL_STATUSES : statuses
  });
});
export const getCampaignChildrenTotals = createSelector(getCampaignChildrenFilter, getChildReports, (filter, childReports) => childReports.get(filter, ChildrenReportRecord()).totals);
export const getCampaignAdGroupFilter = createSelector(getGlobalFilters, getParams, getNetworkFromParams, ({
  timeRange,
  attributionView
}, {
  campaignId
}, network) => CampaignFilterRecord({
  timeRange,
  entityId: campaignId,
  entityType: EntityTypes.ADGROUP,
  network,
  attributionView,
  statuses: ALL_STATUSES
}));
export const getAdGroup = createSelector(getChildrenByEntity, getCampaignAdGroupFilter, (childrenByEntity, filter) => {
  const children = childrenByEntity.get(filter, null);
  return children;
});
export const getChildrenFromProps = createSelector(getChildrenByEntity, getCampaignChildrenFilter, getEntityData, (childrenByEntity, filter, {
  entityType
}) => {
  const children = childrenByEntity.get(filter, null);

  if (children && entityType === EntityTypes.NEGATIVE_KEYWORD) {
    return children.filter(child => child.entity.negative);
  }

  if (children && entityType === EntityTypes.KEYWORD) {
    return children.filter(child => !child.entity.negative);
  }

  return children;
});
export const getCampaignAdGroupChildren = createSelector(getChildrenByEntity, getCampaignAdGroupFilter, (childrenByEntity, filter) => childrenByEntity.get(filter, ImmutableMap()));
export const getCampaignTableAdGroup = createSelector(getCampaignAdGroupChildren, getAdGroupIdFromParams, (children, adGroupId) => children.get(adGroupId));
export const getCampaignTableNoAdGroup = createSelector(getChildrenByEntity, getCampaignAdGroupFilter, getAdGroupIdFromParams, (childrenByEntity, filter, adGroupId) => {
  const children = childrenByEntity.get(filter);

  if (!children) {
    return false; // Still fetching
  }

  return !children.keySeq().includes(adGroupId);
});
export const getAdSetsByEntityRequestStatus = state => {
  return state.requestStatus.adSets;
};
export const getAdSetsRequestStatus = createSelector(getAdSetsByEntityRequestStatus, getCampaignAdGroupFilter, (childrenByEntityRequestStatus, filter) => childrenByEntityRequestStatus.get(filter, RequestStatusRecord()));
export const getChildrenByEntityRequestStatus = state => state.requestStatus.reportingChildren;
export const getChildrenRequestStatus = createSelector(getChildrenByEntityRequestStatus, getCampaignChildrenFilter, (childrenByEntityRequestStatus, filter) => childrenByEntityRequestStatus.get(filter, RequestStatusRecord()));
export const getCampaignAdGroupRequestStatus = createSelector(getChildrenByEntityRequestStatus, getCampaignAdGroupFilter, (childrenByEntityRequestStatus, filter) => childrenByEntityRequestStatus.get(filter, RequestStatusRecord()));
export const getAdSequenceMap = state => state.reporting.sequences;
export const getAdSequence = createSelector(getAdSequenceMap, getSequenceIdFromParams, getGlobalFilters, (adSequenceMap, sequenceId, {
  attributionView,
  timeRange
}) => adSequenceMap.getIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView]));
export const getAdSequenceRSMap = state => state.requestStatus.sequenceDetails;
export const getAdSequenceRS = createSelector(getAdSequenceRSMap, getSequenceIdFromParams, getGlobalFilters, (adSequenceRSMap, sequenceId, {
  attributionView,
  timeRange
}) => adSequenceRSMap.getIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView], RequestStatusRecord()));